import { ApiResponse } from '../types';
import { itemService, LocalItem, ExternalItem } from './itemService';
import { supabase } from '../utils/supabase';

// Delivery Person Types
export interface DeliveryPerson {
  delivery_hash: string;
  name: string;
  code: string;
  active: boolean;
  deleted: boolean;
  avatar_url?: string;
  phone_number?: string;
  telegram_id?: string;
  created_at: string;
  updated_at: string;
}

export interface DeliveryPersonListResponse {
  delivery_people: DeliveryPerson[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  };
  filters: {
    show_deleted: boolean;
    active_only: boolean;
  };
}

export interface DeliveryPersonApiResponse {
  status: number;
  data: {
    message: string;
    data: DeliveryPersonListResponse;
  };
}

// Delivery Person Orders and Items
export interface DeliveryPersonOrder {
  orderNumber: string;
  quantity: number;
  orderStatus: string;
  rate?: number;
}

export interface DeliveryPersonItem {
  sku: string;
  name?: string;
  totalQuantity: number;
  rate: number;
  totalAmount: number;
  items: DeliveryPersonOrder[];
  // Image and additional info from stock room or Hike POS
  image_url?: string;
  primary_image?: string;
  brand?: string;
  category?: string;
  barcode?: string;
  current_stock?: number;
  source?: 'stock_room' | 'hike_pos' | 'billarea_api' | 'none';
}

export interface DeliveryPersonOrdersResponse {
  status: number;
  data: {
    message: string;
    orders: any[];
  };
}

export interface DeliveryPersonItemsResponse {
  status: number;
  data: {
    message: string;
    items: DeliveryPersonItem[];
  };
}

export class DeliveryPersonService {
  private static readonly BASE_URL = 'https://order.beautello.mv/NewProject/src/api/v1/deliveryman';
  private static readonly ORDER_BASE_URL = 'https://order.beautello.mv/NewProject/src/api/v1';
  
  // Cache for delivery person items
  private static deliveryPersonItemsCache = new Map<string, {
    data: DeliveryPersonItem[];
    timestamp: number;
    loading: boolean;
    error: string | null;
  }>();
  
  private static readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private static readonly CACHE_KEY_PREFIX = 'delivery_person_items_';

  // Sync delivery person to database
  static async syncDeliveryPersonToDatabase(deliveryPerson: DeliveryPerson): Promise<{ success: boolean; error?: string }> {
    try {
      const { data, error } = await supabase.rpc('sync_delivery_person', {
        p_delivery_hash: deliveryPerson.delivery_hash,
        p_name: deliveryPerson.name,
        p_code: deliveryPerson.code,
        p_phone: deliveryPerson.phone_number,
        p_avatar_url: deliveryPerson.avatar_url,
        p_store_id: null // We can set this to a specific store if needed
      });

      if (error) {
        console.error('Error syncing delivery person to database:', error);
        return { success: false, error: error.message };
      }

      if (data && !data.success) {
        console.error('RPC error syncing delivery person:', data.error);
        return { success: false, error: data.error };
      }

      console.log('✅ Delivery person synced to database:', deliveryPerson.name);
      return { success: true };
    } catch (error: any) {
      console.error('Exception syncing delivery person:', error);
      return { success: false, error: error.message };
    }
  }

  // Get active delivery persons list
  static async getActiveDeliveryPersons(
    page = 1,
    limit = 50
  ): Promise<ApiResponse<DeliveryPerson[]>> {
    try {
      const url = new URL(`${this.BASE_URL}/getDeliveryManList.php`);
      url.searchParams.append('page', page.toString());
      url.searchParams.append('limit', limit.toString());
      url.searchParams.append('active_only', 'true');
      url.searchParams.append('show_deleted', 'false');

      console.log('Fetching delivery persons from:', url.toString());

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: DeliveryPersonApiResponse = await response.json();
      
      console.log('Delivery persons API response:', result);

      if (result.status === 1 && result.data?.data?.delivery_people) {
        // Filter only active delivery persons
        const activePersons = result.data.data.delivery_people.filter(
          person => person.active && !person.deleted
        );

        return {
          data: activePersons,
          error: null
        };
      } else {
        return {
          data: [],
          error: result.data?.message || 'Failed to fetch delivery persons'
        };
      }
    } catch (error: any) {
      console.error('Error fetching delivery persons:', error);
      return {
        data: [],
        error: error.message || 'Failed to fetch delivery persons'
      };
    }
  }

  // Get delivery person by hash
  static async getDeliveryPersonByHash(
    deliveryHash: string
  ): Promise<ApiResponse<DeliveryPerson | null>> {
    try {
      // Get all active persons and find by hash
      const result = await this.getActiveDeliveryPersons(1, 100);
      
      if (result.error) {
        return { data: null, error: result.error };
      }

      const person = result.data?.find(p => p.delivery_hash === deliveryHash);
      
      return {
        data: person || null,
        error: person ? null : 'Delivery person not found'
      };
    } catch (error: any) {
      return {
        data: null,
        error: error.message || 'Failed to fetch delivery person'
      };
    }
  }

  // Format delivery person for display
  static formatDeliveryPersonName(person: DeliveryPerson): string {
    return `${person.name} (${person.code})`;
  }

  // Get delivery person display info
  static getDeliveryPersonDisplayInfo(person: DeliveryPerson) {
    return {
      id: person.delivery_hash,
      name: person.name,
      code: person.code,
      displayName: this.formatDeliveryPersonName(person),
      phone: person.phone_number,
      avatar: person.avatar_url,
      isActive: person.active && !person.deleted
    };
  }

  // Validate delivery person is active
  static async validateDeliveryPerson(
    deliveryHash: string
  ): Promise<ApiResponse<boolean>> {
    try {
      const result = await this.getDeliveryPersonByHash(deliveryHash);
      
      if (result.error) {
        return { data: false, error: result.error };
      }

      const isValid = result.data?.active && !result.data?.deleted;
      
      return {
        data: !!isValid,
        error: isValid ? null : 'Delivery person is not active'
      };
    } catch (error: any) {
      return {
        data: false,
        error: error.message || 'Failed to validate delivery person'
      };
    }
  }

  // Cache management methods
  private static getCacheKey(deliveryHash: string): string {
    return `${this.CACHE_KEY_PREFIX}${deliveryHash}`;
  }

  private static isCacheValid(cacheEntry: any): boolean {
    return cacheEntry && (Date.now() - cacheEntry.timestamp) < this.CACHE_DURATION;
  }

  private static getCachedItems(deliveryHash: string): DeliveryPersonItem[] | null {
    const cacheKey = this.getCacheKey(deliveryHash);
    const cacheEntry = this.deliveryPersonItemsCache.get(cacheKey);
    
    if (cacheEntry && this.isCacheValid(cacheEntry)) {
      console.log('📦 Using cached delivery person items for:', deliveryHash);
      return cacheEntry.data;
    }
    
    return null;
  }

  private static setCachedItems(deliveryHash: string, data: DeliveryPersonItem[], error: string | null = null): void {
    const cacheKey = this.getCacheKey(deliveryHash);
    this.deliveryPersonItemsCache.set(cacheKey, {
      data,
      timestamp: Date.now(),
      loading: false,
      error
    });
    console.log('💾 Cached delivery person items for:', deliveryHash, '- Items:', data.length);
  }

  private static setCacheLoading(deliveryHash: string, loading: boolean): void {
    const cacheKey = this.getCacheKey(deliveryHash);
    const existing = this.deliveryPersonItemsCache.get(cacheKey);
    if (existing) {
      existing.loading = loading;
    } else {
      this.deliveryPersonItemsCache.set(cacheKey, {
        data: [],
        timestamp: 0,
        loading,
        error: null
      });
    }
  }

  private static isCacheLoading(deliveryHash: string): boolean {
    const cacheKey = this.getCacheKey(deliveryHash);
    const cacheEntry = this.deliveryPersonItemsCache.get(cacheKey);
    return cacheEntry?.loading || false;
  }

  /**
   * Clear cache for a specific delivery person or all cached data
   */
  static clearCache(deliveryHash?: string): void {
    if (deliveryHash) {
      const cacheKey = this.getCacheKey(deliveryHash);
      this.deliveryPersonItemsCache.delete(cacheKey);
      console.log('🗑️ Cleared cache for delivery person:', deliveryHash);
    } else {
      this.deliveryPersonItemsCache.clear();
      console.log('🗑️ Cleared all delivery person items cache');
    }
  }

  /**
   * Force refresh delivery person items (bypasses cache)
   */
  static async refreshDeliveryPersonItems(
    deliveryHash: string,
    onItemUpdate?: (updatedItem: DeliveryPersonItem) => void
  ): Promise<ApiResponse<DeliveryPersonItem[]>> {
    // Clear cache first
    this.clearCache(deliveryHash);
    // Then load fresh data
    return this.getDeliveryPersonItems(deliveryHash, onItemUpdate);
  }

  // Get delivery person item list by connecting two APIs (with caching)
  static async getDeliveryPersonItems(
    deliveryHash: string,
    onItemUpdate?: (updatedItem: DeliveryPersonItem) => void
  ): Promise<ApiResponse<DeliveryPersonItem[]>> {
    try {
      console.log('Fetching delivery person items for hash:', deliveryHash);

      // Check if already loading to prevent duplicate requests
      if (this.isCacheLoading(deliveryHash)) {
        console.log('⏳ Already loading delivery person items for:', deliveryHash);
        return { data: [], error: 'Already loading' };
      }

      // Check cache first
      const cachedItems = this.getCachedItems(deliveryHash);
      if (cachedItems) {
        // Return cached data immediately, but still trigger live enhancement if callback provided
        if (onItemUpdate) {
          console.log('🔄 Using cache but triggering live enhancements...');
          // Run enhancements in background without blocking
          setTimeout(() => {
            this.enhanceItemsWithStockInfoAsync(cachedItems, onItemUpdate);
          }, 0);
        }
        return { data: cachedItems, error: null };
      }

      // Set loading state
      this.setCacheLoading(deliveryHash, true);

      // Step 1: Get orders from getMyListEnhanced.php
      const orders = await this.getDeliveryPersonOrders(deliveryHash);
      if (orders.error) {
        this.setCacheLoading(deliveryHash, false);
        return { data: [], error: orders.error };
      }

      console.log('Found orders:', orders.data.length);

      // Step 2: Get items for each order from billArea.php and group by SKU
      const itemsMap = new Map<string, DeliveryPersonItem>();

      for (const order of orders.data) {
        try {
          const orderItems = await this.getOrderItems(order.order_id);
          if (orderItems.error) {
            console.warn(`Failed to get items for order ${order.order_id}:`, orderItems.error);
            continue;
          }

          // Process each item in the order
          for (const item of orderItems.data) {
            const sku = item.item_sku;
            const quantity = parseInt(item.quantity) || 0;
            const rate = parseFloat(item.rate) || 0;

            // Images already available from billArea API!

            if (itemsMap.has(sku)) {
              // Add to existing item
              const existingItem = itemsMap.get(sku)!;
              existingItem.totalQuantity += quantity;
              existingItem.totalAmount += (quantity * rate);
              existingItem.items.push({
                orderNumber: order.order_number,
                quantity: quantity,
                orderStatus: order.order_status || 'Unknown',
                rate: rate
              });
            } else {
              // Create new item entry with images directly from billArea API
              itemsMap.set(sku, {
                sku: sku,
                name: item.name || item.item_name || sku,
                totalQuantity: quantity,
                rate: rate,
                totalAmount: quantity * rate,
                // Use images directly from billArea API (instant!)
                image_url: item.photo_url || item.image_url,
                primary_image: item.photo_url || item.image_url,
                brand: item.brand,
                category: item.category,
                barcode: item.barcode,
                source: 'billarea_api',
                items: [{
                  orderNumber: order.order_number,
                  quantity: quantity,
                  orderStatus: order.order_status || 'Unknown',
                  rate: rate
                }]
              });
            }
          }
        } catch (itemError) {
          console.warn(`Error processing order ${order.order_id}:`, itemError);
        }
      }

      // Convert map to array
      const result = Array.from(itemsMap.values());
      console.log('Grouped items by SKU:', result.length, 'unique items');

      // Cache the results
      this.setCachedItems(deliveryHash, result);

      // Step 3: Return immediately with images, enhance with stock data LIVE
      // Fast loading: items already have images from billArea API!
      const enhancementPromise = this.enhanceItemsWithStockInfoAsync(result, onItemUpdate);

      return {
        data: result,
        error: null,
        enhancementPromise // Return the promise so components can listen for completion
      };
    } catch (error: any) {
      console.error('Error fetching delivery person items:', error);
      this.setCacheLoading(deliveryHash, false);
      return {
        data: [],
        error: error.message || 'Failed to fetch delivery person items'
      };
    }
  }

  // Live stock enhancement - update each item as soon as it's ready
  private static enhanceItemsWithStockInfoAsync(
    items: DeliveryPersonItem[], 
    onItemUpdate?: (updatedItem: DeliveryPersonItem) => void
  ): Promise<void> {
    console.log('Starting live enhancement for', items.length, 'items (loading from Supabase: names, descriptions, stock)');
    
    // Return a promise that resolves when all enhancements are complete
    return new Promise((resolve) => {
      setTimeout(async () => {
        try {
          // Process each item and update UI immediately when ready
          const stockPromises = items.map(async (item, index) => {
            try {
              // Get item data from Supabase stock room only
              const localResult = await itemService.searchLocalItems(item.sku);

              let itemUpdated = false;

              // Add stock information and item details from stock room
              if (localResult.data && localResult.data.length > 0) {
                const localItem = localResult.data[0];
                console.log(`📦 Live: ${item.sku} - Stock level:`, localItem.current_stock, '- Name:', localItem.name);

                // Use Supabase item data (name, description, stock, etc.)
                item.name = localItem.name || item.name;
                item.current_stock = localItem.current_stock;
                item.brand = localItem.brand || item.brand;
                item.category = localItem.category || item.category;
                item.barcode = localItem.barcode || item.barcode;
                item.source = 'stock_room';
                itemUpdated = true;
              }

              // 🔥 LIVE UPDATE: Notify UI immediately if item was enhanced
              if (itemUpdated && onItemUpdate) {
                onItemUpdate(item);
              }

            } catch (error) {
              console.warn(`❌ Enhancement failed for ${item.sku}:`, error);
            }
          });
          
          // Wait for all stock lookups to complete
          await Promise.allSettled(stockPromises);
          console.log('🎉 Live enhancement complete - all items loaded from Supabase with names, descriptions, and stock levels');
          
          // Resolve the promise to notify components that all items are done
          resolve();
          
        } catch (error) {
          console.error('Live enhancement process failed:', error);
          resolve(); // Still resolve to avoid hanging
        }
      }, 0); // Start immediately but don't block
    });
  }

  // Step 1: Get delivery person orders (non-delivered) - Updated to match your guide
  private static async getDeliveryPersonOrders(
    deliveryHash: string
  ): Promise<ApiResponse<any[]>> {
    try {
      const url = new URL(`${this.BASE_URL}/getMyListEnhanced.php`);
      url.searchParams.append('delivery_hash', deliveryHash);
      url.searchParams.append('show_delivered', 'false');
      url.searchParams.append('limit', '100');

      console.log('Fetching orders from:', url.toString());
      console.log('BASE_URL:', this.BASE_URL);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Orders API response:', result);

      if (result.status === 1 && result.data?.data?.list_items) {
        return {
          data: result.data.data.list_items,
          error: null
        };
      } else {
        return {
          data: [],
          error: result.message || 'No orders found for delivery person'
        };
      }
    } catch (error: any) {
      console.error('Error fetching delivery person orders:', error);
      return {
        data: [],
        error: error.message || 'Failed to fetch delivery person orders'
      };
    }
  }

  // Step 2: Get items for a specific order - Updated to match your guide
  private static async getOrderItems(
    orderId: string
  ): Promise<ApiResponse<any[]>> {
    try {
      const url = new URL(`${this.ORDER_BASE_URL}/orders/billArea.php`);
      url.searchParams.append('id', orderId);

      console.log('Fetching order items from:', url.toString());

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Order items API response for', orderId, ':', result);

      if (result.status === 1 && result.data?.data?.bills) {
        // Extract items from bills according to your guide
        const allItems: any[] = [];
        result.data.data.bills.forEach((bill: any) => {
          if (bill.bill_details?.items) {
            bill.bill_details.items.forEach((item: any) => {
              // Extract items with images directly from billArea API
              
              allItems.push({
                item_sku: item.item_sku,
                quantity: item.quantity,
                rate: item.rate,
                total: item.total,
                name: item.name || item.item_name,
                // Use photo_url directly from billArea API (instant images!)
                photo_url: item.photo_url,
                image_url: item.photo_url, // Use same for compatibility
                brand: item.brand,
                category: item.category,
                barcode: item.barcode
              });
            });
          }
        });

        return {
          data: allItems,
          error: null
        };
      } else {
        return {
          data: [],
          error: result.message || `No items found for order ${orderId}`
        };
      }
    } catch (error: any) {
      console.error(`Error fetching items for order ${orderId}:`, error);
      return {
        data: [],
        error: error.message || `Failed to fetch items for order ${orderId}`
      };
    }
  }

  // Session Delivery Person Items Management

  /**
   * Sync delivery person items to local items table for better performance
   */
  static async syncItemsToLocal(
    deliveryItems: DeliveryPersonItem[],
    userId?: string
  ): Promise<ApiResponse<{ synced_count: number; updated_count: number; total_processed: number }>> {
    try {
      const { data, error } = await supabase.rpc('sync_delivery_person_items_to_local', {
        p_delivery_items: deliveryItems,
        p_user_id: userId
      });

      if (error) {
        console.error('Error syncing delivery person items to local:', error);
        return { data: { synced_count: 0, updated_count: 0, total_processed: 0 }, error: error.message };
      }

      if (!data?.success) {
        console.error('RPC error syncing delivery person items:', data?.error);
        return { data: { synced_count: 0, updated_count: 0, total_processed: 0 }, error: data?.error || 'Failed to sync delivery person items' };
      }

      return { data: data.data, error: null };
    } catch (error: any) {
      console.error('Exception syncing delivery person items to local:', error);
      return { data: { synced_count: 0, updated_count: 0, total_processed: 0 }, error: error.message };
    }
  }

  /**
   * Import delivery person items to session (replaces existing list)
   */
  static async importItemsToSession(
    sessionId: string,
    deliveryItems: DeliveryPersonItem[],
    userId?: string
  ): Promise<ApiResponse<{ imported_count: number; inserted_count: number; updated_count: number }>> {
    try {
      // First, sync items to local database for better performance
      console.log('🔄 Syncing delivery person items to local database for better performance...');
      const syncResult = await this.syncItemsToLocal(deliveryItems, userId);
      if (syncResult.data && syncResult.data.total_processed > 0) {
        console.log('✅ Synced items to local database:', syncResult.data);
      }

      const { data, error } = await supabase.rpc('import_delivery_person_items_to_session', {
        p_session_id: sessionId,
        p_delivery_items: deliveryItems,
        p_user_id: userId
      });

      if (error) {
        console.error('Error importing delivery person items to session:', error);
        return { data: { imported_count: 0, inserted_count: 0, updated_count: 0 }, error: error.message };
      }

      if (!data?.success) {
        console.error('RPC error importing delivery person items:', data?.error);
        return { data: { imported_count: 0, inserted_count: 0, updated_count: 0 }, error: data?.error || 'Failed to import delivery person items' };
      }

      return { data: data.data, error: null };
    } catch (error: any) {
      console.error('Exception importing delivery person items to session:', error);
      return { data: { imported_count: 0, inserted_count: 0, updated_count: 0 }, error: error.message };
    }
  }

  /**
   * Get delivery person items for a session
   */
  static async getSessionDeliveryPersonItems(
    sessionId: string,
    userId?: string
  ): Promise<ApiResponse<any[]>> {
    try {
      const { data, error } = await supabase.rpc('get_session_delivery_person_items', {
        p_session_id: sessionId,
        p_user_id: userId
      });

      if (error) {
        console.error('Error getting session delivery person items:', error);
        return { data: [], error: error.message };
      }

      if (!data?.success) {
        console.error('RPC error getting session delivery person items:', data?.error);
        return { data: [], error: data?.error || 'Failed to get session delivery person items' };
      }

      return { data: data.data?.items || [], error: null };
    } catch (error: any) {
      console.error('Exception getting session delivery person items:', error);
      return { data: [], error: error.message };
    }
  }

  /**
   * Remove delivery person item from session
   */
  static async removeItemFromSession(
    sessionId: string,
    itemSku: string,
    userId?: string
  ): Promise<ApiResponse<{ deleted: boolean; deleted_count: number }>> {
    try {
      const { data, error } = await supabase.rpc('remove_delivery_person_item_from_session', {
        p_session_id: sessionId,
        p_item_sku: itemSku,
        p_user_id: userId
      });

      if (error) {
        console.error('Error removing delivery person item from session:', error);
        return { data: { deleted: false, deleted_count: 0 }, error: error.message };
      }

      if (!data?.success) {
        console.error('RPC error removing delivery person item:', data?.error);
        return { data: { deleted: false, deleted_count: 0 }, error: data?.error || 'Failed to remove delivery person item' };
      }

      return { data: data.data, error: null };
    } catch (error: any) {
      console.error('Exception removing delivery person item from session:', error);
      return { data: { deleted: false, deleted_count: 0 }, error: error.message };
    }
  }

  /**
   * Clear all delivery person items from session
   */
  static async clearSessionDeliveryPersonItems(
    sessionId: string,
    userId?: string
  ): Promise<ApiResponse<{ cleared_count: number }>> {
    try {
      const { data, error } = await supabase.rpc('clear_session_delivery_person_items', {
        p_session_id: sessionId,
        p_user_id: userId
      });

      if (error) {
        console.error('Error clearing session delivery person items:', error);
        return { data: { cleared_count: 0 }, error: error.message };
      }

      if (!data?.success) {
        console.error('RPC error clearing session delivery person items:', data?.error);
        return { data: { cleared_count: 0 }, error: data?.error || 'Failed to clear session delivery person items' };
      }

      return { data: data.data, error: null };
    } catch (error: any) {
      console.error('Exception clearing session delivery person items:', error);
      return { data: { cleared_count: 0 }, error: error.message };
    }
  }

  /**
   * Get delivery person items for a completed movement
   */
  static async getMovementDeliveryPersonItems(
    movementId: string,
    userId?: string
  ): Promise<ApiResponse<any[]>> {
    try {
      const { data, error } = await supabase.rpc('get_movement_delivery_person_items', {
        p_movement_id: movementId,
        p_user_id: userId
      });

      if (error) {
        console.error('Error getting movement delivery person items:', error);
        return { data: [], error: error.message };
      }

      if (!data?.success) {
        console.error('RPC error getting movement delivery person items:', data?.error);
        return { data: [], error: data?.error || 'Failed to get movement delivery person items' };
      }

      return { data: data.data?.items || [], error: null };
    } catch (error: any) {
      console.error('Exception getting movement delivery person items:', error);
      return { data: [], error: error.message };
    }
  }
}

